import { BuildingOfficeIcon } from "@heroicons/react/24/solid";
import { useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useHistory } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import DownloadButton from "../../../../components/DownloadButton";
import MaterialTableTS from "../../../../components/tables/MaterialTableTS";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { OfficeTabs } from "../../../OfficeTab/OfficeTabs";
import { mainSelectedTab } from "../../atoms";
import useGetOfficeHierarchy from "./hooks/useGetOfficeHierarchy";
import { accountAtom } from "../../../Login/atoms/AccountAtom";

interface OfficeHierarchyReportProps {}
export const OfficeHierarchyReport: React.FC<OfficeHierarchyReportProps> =
  () => {
    const [exportData, setExportData] = useState([]);
    const { data: officeHData, isLoading: officeHierarchyLoading } =
      useGetOfficeHierarchy();
    const tableRef = useRef<any>([]);
    const [, setSelectedIndex] = useRecoilState(mainSelectedTab);
    const history = useHistory();
    const userPermissions = useRecoilValue(permissionsSelector);
    const user = useRecoilValue(accountAtom);

    interface IOfficeHierarchy {
      officeType: string;
      salesTypeC: string;
      channelC: string;
      officeName: string;
      officeCode: string;
      storeNumber: string;
      officeStatus: string;
      address: string;
      city: string;
      state: string;
      zipCode: string;
      brandMarketingName: string;
      branchManager: string;
      districtCode: string;
      districtName: string;
      districtManager: string;
      regionCode: string;
      regionName: string;
      areaCode: string;
      areaName: string;
      salesVp: string;
      territoryCode: string;
      territoryName: string;
      salesPresident: string;
      brandName: string;
    }

    //For downloading table data
    const mapDataToExport = (data) => {
      setExportData(
        data?.map((ohr: IOfficeHierarchy) => {
          return {
            Type: ohr.officeType,
            "Sales Type": ohr.salesTypeC,
            Channel: ohr.channelC,
            "Corporate Office #": ohr.storeNumber,
            "Office Code": ohr.officeCode,
            Brand: ohr.brandName,
            "Office Name": ohr.officeName,
            "Office Status": ohr.officeStatus,
            State: ohr.state,
            "Branch Manager": ohr.branchManager,
            "Office District": ohr.districtCode,
            "District Name": ohr.districtName,
            "Office District Manager": ohr.districtManager,
            "Office Region": ohr.regionCode,
            "Region Name": ohr.regionName,
            "Office Area": ohr.areaCode,
            "Area Name": ohr.areaName,
            "Office Sales VP": ohr.salesVp,
            "Office Territory": ohr.territoryCode,
            "Territory Name": ohr.territoryName,
            "Office Sales President": ohr.salesPresident,
          };
        })
      );
    };
    let dateTimeToday = new Date(new Date().toLocaleString()).toLocaleString();
    const csvReport = {
      filename: "OfficeHierarchyReport_" + dateTimeToday + ".csv",
      data: exportData,
    };
    const officeColumns = [
      { title: "Type", field: "officeType" },
      { title: "Sales Type", field: "salesTypeC" },
      { title: "Channel", field: "channelC" },
      { title: "Corporate Office #", field: "storeNumber" },
      { title: "Office Code", field: "officeCode" },
      { title: "Brand", field: "brandName" },
      {
        title: "Office Name",
        field: "officeName",
        render: (rowData) => {
          return (
            <div
              className="cursor-pointer"
              onClick={() => {
                const url = "/officesettings/officesetting";
                history.push({
                  pathname: url,
                  state: {
                    pathname: url,
                    officeCode: rowData?.officeCode,
                    action: "OfficeHierarchy",
                  },
                });
                window.location.reload();
                let userTabs = [
                  {
                    to: "",
                  },
                ];
                [...OfficeTabs].forEach((tabs) => {
                  let temp = [...userPermissions].filter(
                    (user) => user === tabs.permission
                  );
                  if (temp.length > 0)
                    userTabs.push({
                      to: tabs.to,
                    });
                });
                userTabs.shift();
                const index = userTabs.findIndex((c) => c.to === url);
                setSelectedIndex(index);
              }}
            >
              <span className="text-quinary  font-bold underline text-sm ">
                {rowData.officeName}
              </span>
            </div>
          );
        },
      },
      { title: "Office Status", field: "officeStatus" },
      { title: "State", field: "state" },
      { title: "Branch Manager", field: "branchManager" },
      { title: "Office District", field: "districtCode" },
      { title: "District Name", field: "districtName" },
      { title: "Office District Manager", field: "districtManager" },
      { title: "Office Region", field: "regionCode" },
      { title: "Region Name", field: "regionName" },
      { title: "Office Area", field: "areaCode" },
      { title: "Area Name", field: "areaName" },
      { title: "Office Sales VP", field: "salesVp" },
      { title: "Office Territory", field: "territoryCode" },
      { title: "Territory Name", field: "territoryName" },
      { title: "Office Sales President", field: "salesPresident" },
    ];

    return (
      <div className="text-xs items-center">
        <div className="">
          <div className="mx-4 mt-4 overflow-x-auto">
            <div className="flex mb-2 gap-2 items-center">
              <div>
                <BuildingOfficeIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
              </div>
              <div className="flex flex-col leading tight">
                <div className="text-gray-500 text-md font-medium">
                  Report: Accounts
                </div>
                <div className="flex items-center font-bold text-lg text-primary">
                  Office Hierarchy Report{" "}
                </div>
              </div>
            </div>
            <div className="mt-2" />
            <div className="flex justify-between mb-2 mr-2">
              <div className="flex items-center gap-1">
                <span className="text-gray-500 text-sm">Total Records: </span>
                <span className="text-primary text-base font-semibold">
                  {
                    officeHData?.filter((x) =>
                      user?.isFranchise && user?.isNonFranchise
                        ? true
                        : user?.isFranchise
                        ? x.officeType?.toLowerCase() === "franchise"
                        : x.officeType?.toLowerCase() !== "franchise"
                    ).length
                  }
                </span>
              </div>
              <CSVLink className="" {...csvReport}>
                <DownloadButton
                  buttonValue="Download File"
                  onClick={() =>
                    mapDataToExport(tableRef.current.dataManager.searchedData)
                  }
                  className={undefined}
                  disabled={undefined}
                />
              </CSVLink>
            </div>
          </div>
          <div className="box-shadow mx-4 mb-4 mt-2 overflow-x-auto grid grid-row">
            <MaterialTableTS
              data={
                officeHData
                  ? officeHData?.filter((x) =>
                      user?.isFranchise && user?.isNonFranchise
                        ? true
                        : user?.isFranchise
                        ? x.officeType?.toLowerCase() === "franchise"
                        : x.officeType?.toLowerCase() !== "franchise"
                    )
                  : []
              }
              isLoading={officeHierarchyLoading}
              columns={officeColumns}
              showTitle={false}
              showToolbar={true}
              toolbarSearch={true}
              searchFieldAlignment="left"
              tableRef={tableRef}
            />
          </div>
        </div>
      </div>
    );
  };
